import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    MenuItem,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import videocallshop from '../../apis/videocallshop';
import {
    createCardToken,
    detectBrand,
    formatCardNumber,
    isValidCardNumber,
    isValidExpiry,
    onlyDigits,
} from '../../lib/payway';

// Formulario de pago con Payway: pedido + tarjeta + resultado.
//
// A diferencia de MercadoPago, que se cobra en su propio sitio, acá el pago
// ocurre en nuestra página. Es lo que nos permite mandarle a Payway NUESTRO
// site_transaction_id y saber en el acto si el pago se aprobó — el checkout
// hosteado de Payway no acepta un identificador propio ni devuelve un id
// consultable, así que un pago hecho ahí no se puede atar a un pedido.
//
// La tarjeta no pasa por nuestro servidor: se tokeniza contra Payway desde el
// navegador (src/lib/payway.js) y al backend sólo viaja un token de un solo uso.
//
// No depende de la ruta: recibe el pedido y el `t` por props. Lo monta la
// página /pagar/:id?t= (link del mail y de "Mis órdenes") y también el diálogo
// de pago dentro de la videollamada (cart/InCallPayDialog), donde navegar
// cortaría la sesión de OpenTok. Sin sesión: el `?t=` es lo que autoriza.

const money = (value) =>
    Number(value || 0).toLocaleString('es-AR', {
        style: 'currency',
        currency: 'ARS',
    });

const PaywayPaymentForm = ({
    purchaseOrderId,
    accessToken,
    onApproved,
    // Botón que acompaña al resultado aprobado (p. ej. "Volver a la llamada").
    approvedAction = null,
}) => {
    const [order, setOrder] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [loading, setLoading] = useState(true);

    const [cardNumber, setCardNumber] = useState('');
    const [expiry, setExpiry] = useState('');
    const [securityCode, setSecurityCode] = useState('');
    const [cardHolderName, setCardHolderName] = useState('');
    const [documentNumber, setDocumentNumber] = useState('');
    const [email, setEmail] = useState('');
    const [brandId, setBrandId] = useState('');

    const [fieldErrors, setFieldErrors] = useState({});
    const [payError, setPayError] = useState(null);
    const [paying, setPaying] = useState(false);
    const [approved, setApproved] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await videocallshop.get(
                `/purchase-orders/${purchaseOrderId}/payment`,
                { params: { t: accessToken } }
            );
            setOrder(data.data);
            setApproved(Boolean(data.data.paid));
        } catch (err) {
            setLoadError(
                (err.response && err.response.data && err.response.data.message) ||
                    'No pudimos abrir este link de pago.'
            );
        } finally {
            setLoading(false);
        }
    }, [purchaseOrderId, accessToken]);

    useEffect(() => {
        load();
    }, [load]);

    // Sólo las marcas que la tienda declaró tener habilitadas en su cuenta de
    // Payway: ofrecer una que no acepta termina en un rechazo, y los rechazos
    // tienen costo (Visa y Mastercard multan los reintentos).
    const brands = (order && order.payway && order.payway.cardBrands) || [];

    // El BIN sólo pre-selecciona; el comprador puede corregir.
    const onCardNumberChange = (value) => {
        setCardNumber(formatCardNumber(value));
        const detected = detectBrand(value);
        if (detected && brands.some((brand) => brand.id === detected.id)) {
            setBrandId(detected.id);
        }
    };

    const onExpiryChange = (value) => {
        const digits = onlyDigits(value).slice(0, 4);
        setExpiry(
            digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits
        );
    };

    const validate = () => {
        const [month, year] = expiry.split('/');
        const errors = {};

        if (!isValidCardNumber(cardNumber)) {
            errors.cardNumber = 'Revisá el número de la tarjeta.';
        }
        if (!isValidExpiry(month, year)) {
            errors.expiry = 'Vencimiento inválido.';
        }
        if (onlyDigits(securityCode).length < 3) {
            errors.securityCode = 'Código incompleto.';
        }
        if (!cardHolderName.trim()) {
            errors.cardHolderName = 'Ingresá el nombre del titular.';
        }
        if (onlyDigits(documentNumber).length < 7) {
            errors.documentNumber = 'Ingresá el DNI del titular.';
        }
        if (!brandId) {
            errors.brandId = 'Elegí la marca de la tarjeta.';
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const pay = async () => {
        setPayError(null);
        if (!validate()) return;

        setPaying(true);
        const [month, year] = expiry.split('/');

        try {
            // Paso 1: la tarjeta va del navegador a Payway y vuelve un token.
            // En modo mock no hay credenciales reales con las que tokenizar, así
            // que se saltea: es lo que permite recorrer el flujo entero antes de
            // que Payway entregue nada.
            const { token, bin } = order.payway.mock
                ? { token: 'mock-token', bin: onlyDigits(cardNumber).slice(0, 6) }
                : await createCardToken({
                      environment: order.payway.environment,
                      publicApiKey: order.payway.publicApiKey,
                      cardNumber,
                      expirationMonth: month,
                      expirationYear: year,
                      securityCode,
                      cardHolderName: cardHolderName.trim(),
                      documentNumber,
                  });

            // Paso 2: nuestro backend cobra ese token con la key privada.
            const { data } = await videocallshop.post(
                `/purchase-orders/${purchaseOrderId}/payment`,
                {
                    token,
                    // El bin lo devuelve la tokenización: Payway lo valida
                    // contra el token y rechaza el cobro si no coincide.
                    bin,
                    paymentMethodId: brandId,
                    installments: 1,
                    email: email.trim() || undefined,
                },
                { params: { t: accessToken } }
            );

            if (data.data.approved) {
                setApproved(true);
                if (onApproved) onApproved(purchaseOrderId);
            } else {
                setPayError(data.data.message || 'No pudimos procesar el pago.');
            }
        } catch (err) {
            // El mensaje de la API va primero: `err.message` en un error de
            // axios es el genérico "Request failed with status code 400", que
            // tapaba el motivo real del rechazo.
            setPayError(
                (err.response &&
                    err.response.data &&
                    err.response.data.message) ||
                    err.message ||
                    'No pudimos procesar el pago.'
            );
        } finally {
            setPaying(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ py: 8, textAlign: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (loadError) {
        return <Alert severity="error">{loadError}</Alert>;
    }

    if (approved) {
        return (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                <CheckCircleOutlineIcon
                    sx={{ fontSize: 64, color: 'success.main', mb: 1 }}
                />
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    ¡Pago realizado!
                </Typography>
                <Typography color="text.secondary">
                    Listo, {order.storeName} ya tiene tu pago confirmado y
                    prepara tu pedido.
                </Typography>
                {approvedAction && <Box sx={{ mt: 3 }}>{approvedAction}</Box>}
            </Paper>
        );
    }

    return (
        <>
            <Paper sx={{ p: 3, mb: 2 }}>
                <Typography variant="overline" color="text.secondary">
                    {order.storeName}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Pedido #{order.purchaseOrderId}
                </Typography>

                {(order.items || []).map((item, index) => (
                    <Box
                        key={index}
                        sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            {item.quantity} × {item.productName}
                        </Typography>
                        <Typography variant="body2">
                            {money(item.unitPrice * item.quantity)}
                        </Typography>
                    </Box>
                ))}

                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontWeight: 700 }}>Total</Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                        {money(order.amount)}
                    </Typography>
                </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                    Datos de la tarjeta
                </Typography>

                <TextField
                    fullWidth
                    label="Número de tarjeta"
                    value={cardNumber}
                    onChange={(e) => onCardNumberChange(e.target.value)}
                    error={Boolean(fieldErrors.cardNumber)}
                    helperText={fieldErrors.cardNumber}
                    inputProps={{ inputMode: 'numeric', autoComplete: 'cc-number' }}
                    sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                        label="Vencimiento"
                        placeholder="MM/AA"
                        value={expiry}
                        onChange={(e) => onExpiryChange(e.target.value)}
                        error={Boolean(fieldErrors.expiry)}
                        helperText={fieldErrors.expiry}
                        inputProps={{ inputMode: 'numeric', autoComplete: 'cc-exp' }}
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        label="Cód. seguridad"
                        value={securityCode}
                        onChange={(e) =>
                            setSecurityCode(onlyDigits(e.target.value).slice(0, 4))
                        }
                        error={Boolean(fieldErrors.securityCode)}
                        helperText={fieldErrors.securityCode}
                        inputProps={{ inputMode: 'numeric', autoComplete: 'cc-csc' }}
                        sx={{ flex: 1 }}
                    />
                </Box>

                <TextField
                    fullWidth
                    select
                    label="Marca"
                    value={brandId}
                    onChange={(e) => setBrandId(e.target.value)}
                    error={Boolean(fieldErrors.brandId)}
                    helperText={fieldErrors.brandId}
                    sx={{ mb: 2 }}
                >
                    {brands.map((brand) => (
                        <MenuItem key={brand.id} value={brand.id}>
                            {brand.label}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    fullWidth
                    label="Nombre del titular"
                    value={cardHolderName}
                    onChange={(e) => setCardHolderName(e.target.value)}
                    error={Boolean(fieldErrors.cardHolderName)}
                    helperText={fieldErrors.cardHolderName}
                    inputProps={{ autoComplete: 'cc-name' }}
                    sx={{ mb: 2 }}
                />

                <TextField
                    fullWidth
                    label="DNI del titular"
                    value={documentNumber}
                    onChange={(e) =>
                        setDocumentNumber(onlyDigits(e.target.value).slice(0, 11))
                    }
                    error={Boolean(fieldErrors.documentNumber)}
                    helperText={fieldErrors.documentNumber}
                    inputProps={{ inputMode: 'numeric' }}
                    sx={{ mb: 2 }}
                />

                <TextField
                    fullWidth
                    label="Email (opcional)"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    inputProps={{ inputMode: 'email', autoComplete: 'email' }}
                    sx={{ mb: 2 }}
                />

                {payError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {payError}
                    </Alert>
                )}

                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={pay}
                    disabled={paying}
                    startIcon={
                        paying ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : (
                            <LockOutlinedIcon />
                        )
                    }
                >
                    {paying ? 'Procesando…' : `Pagar ${money(order.amount)}`}
                </Button>

                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', textAlign: 'center', mt: 2 }}
                >
                    Los datos de tu tarjeta viajan directo al procesador de
                    pagos. No los guardamos ni pasan por nuestros servidores.
                </Typography>
            </Paper>
        </>
    );
};

export default PaywayPaymentForm;
